<template>
  <div>
    <div class="tr header" v-if="props.index === 0">
      <div v-for="(col,colIndex) in props.cols" :style="col.colStyle" :key="colIndex">
        <div
v-if="col.sortable" :data-tippy-content="'order by' + col.colName"
             class="th sortable-row" @click="props.toggleOrder?.(col.colKey)"
>
          {{col.colName}}
          <font-awesome-icon v-if="props.orderBy === col.colKey" :icon="`caret-${props.ascDesc === 'asc' ? 'up' : 'down'}`"/>
        </div>
        <div class="th" v-else>{{col.colName}}</div>
      </div>
      <div v-if="$slots.actions || $slots['collapse-data']" class="th actions">
        <ExpandButton class="btn btn-primary btn-sm" v-if="$slots['collapse-data']" v-model:model-value="expand"/>
        <slot name="actions"/>
      </div>
    </div>


    <div class="tr" :class="{even: props.index % 2 === 0 && props.banded}">
      <div class="sub-tr">
        <div v-for="(col,colIndex) in props.cols" :style="col.colStyle" :key="colIndex">
          <slot name="cell" :col="col"/>
        </div>
        <div v-if="$slots.actions || $slots['collapse-data']" class="td actions">
          <ExpandButton class="btn btn-primary btn-sm" v-if="$slots['collapse-data']" v-model:model-value="expand"/>
          <slot name="actions"/>
        </div>
      </div>
      <BCollapse v-model="expand" :id="'b-collapse-mobile-row-' + props.index" class="w-100">
        <slot name="collapse-data"/>
      </BCollapse>
    </div>

    <slot name="error"/>
  </div>
  
</template>

<!--
  This component is designed to abstract some of the common features away when building a mobile friendly version
  of a row component.

  Props
  index - the row index from the parent
  item - the row data object
  cols - an object with type Column[] describing the columns that are shown on mobile (not including collapsed content)
  orderBy - column to order by, must exist in cols
  ascDesc - order direction
  toggleOrder - function to switch the ordering of rows


  Slots
  cell - each cell will go into one of these slots.  The styling of the cell is controlled by the parent

  actions - any actions beyond the row toggle need to be added here. The styling and logic are controlled by the parent

  collapse-data - the row information that is concealed by the collapsible button lives here. Again styling controlled
   by the parent

-->

<script setup lang="ts">
import { ref } from 'vue';
import { BCollapse } from 'bootstrap-vue-next';
import ExpandButton from "@/components/ExpandButton.vue";
import { Column } from "@/types";

interface Props {
  index?: number;
  item?: object;
  cols?: Column[];
  orderBy?: string | null;
  ascDesc?: 'asc' | 'desc';
  toggleOrder?: ((colKey: string) => void) | null;
  banded?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  item: () => ({}),
  cols: () => [],
  orderBy: null,
  ascDesc: 'asc',
  toggleOrder: null,
  banded: true
});

const expand = ref(false);

</script>

<style scoped lang="scss">
@use "@/variables.scss" as *;

  .tr {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    text-align: left;
    justify-content: space-between;
    margin-bottom: 10px;
    align-items: center;
  }

  .sub-tr{
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .tr.even {
    background-color: $bgColourLight;
  }

  .tr.header {
    border-bottom: 1px solid $bgColour;
  }

  .th {
    color: $bgColour;
    font-weight: bold;
    padding: 3px;
  }

  .td.actions, .th.actions {
    padding: 3px;
    display: flex;
    justify-content: flex-end;
    align-items:flex-start;
    float: right;
    margin-left:auto;
  }

  .td.actions button, .th.actions button {
    margin:2px;
  }

  .th.actions {
    visibility: hidden;
    height: 0;
  }
  
  .th.sortable-row {
    cursor: pointer;}

</style>